import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, MoreHorizontal, Plus, RotateCcw, Trash2 } from 'lucide-react';
import DropdownMenu, { MenuItem } from './DropdownMenu';

export const RECIPE_ACTION_BLUE = '#3191C8';
export const RECIPE_PRIMARY_PURPLE = '#655FA8';
export const RECIPE_BORDER_GRAY = '#D1D1D1';
export const RECIPE_SURFACE_GRAY = '#F5F5F5';

export const recipeTabActiveClass =
  'bg-white border border-[#D1D1D1] border-b-white text-gray-900 font-medium -mb-px z-10';
export const recipeTabInactiveClass =
  'bg-[#F5F5F5] border-transparent text-gray-600 hover:bg-white hover:border-[#D1D1D1]';

export const recipeAddTabButtonClass =
  'mb-1.5 inline-flex items-center justify-center w-9 h-9 rounded-full border border-[#3191C8] bg-white text-[#3191C8] hover:bg-[#3191C8]/5 transition-colors disabled:opacity-50';

export const recipeAddAttributePillClass =
  'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-[#3191C8] bg-transparent text-[#3191C8] text-sm font-medium hover:bg-[#3191C8]/5 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';

export interface RecipeCardBrandOption {
  id: number;
  name: string;
  is_undefined_default: boolean;
}

export interface RecipeCardFormatOption {
  id: number;
  name: string;
  is_undefined_default: boolean;
}

export interface RecipeCardTabItem {
  id: number;
  tab_name: string;
  brand: number;
  brand_name: string;
  brand_locked: boolean;
  format: number;
  format_name: string;
  format_locked: boolean;
  /** configuring = state 1; options = state 2 */
  phase?: 'configuring' | 'options';
  generation_method?: string;
  display_order: number;
}

interface RecipeCardTabControlsProps {
  tabs: RecipeCardTabItem[];
  activeTabId: number | null;
  brands: RecipeCardBrandOption[];
  formats: RecipeCardFormatOption[];
  canSave: boolean;
  busy?: boolean;
  onSelectTab: (tabId: number) => void;
  onCreateTab: () => void;
  onRenameTab: (tabId: number, tabName: string) => void;
  onDeleteTab: (tabId: number) => void;
  onSelectBrand: (tabId: number, brandId: number) => void;
  onSelectFormat: (tabId: number, formatId: number) => void;
  onCreateBrand: (name: string) => Promise<void>;
  onCreateFormat: (name: string) => Promise<void>;
  onGenerateFormatIdeas?: () => void;
  showBrandFormat?: boolean;
  headerTrailing?: React.ReactNode;
}

function CatalogDropdown({
  label,
  options,
  selectedId,
  locked,
  disabled,
  addPlaceholder,
  onSelect,
  onAdd,
}: {
  label: string;
  options: Array<{ id: number; name: string; is_undefined_default?: boolean }>;
  selectedId: number | null;
  locked: boolean;
  disabled: boolean;
  addPlaceholder: string;
  onSelect: (id: number) => void;
  onAdd: (name: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = options.find((item) => item.id === selectedId);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleAdd = async () => {
    const name = draft.trim();
    if (!name || adding) return;
    setAdding(true);
    try {
      await onAdd(name);
      setDraft('');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div ref={rootRef} className="relative min-w-[160px]">
      <button
        type="button"
        disabled={disabled || locked}
        onClick={() => !locked && setOpen((value) => !value)}
        className={`w-full rounded-lg border border-[#D1D1D1] bg-white pl-4 pr-9 py-2 text-sm text-left transition-colors ${
          locked
            ? 'text-gray-500 cursor-default'
            : 'text-gray-700 hover:border-[#655FA8]/40 cursor-pointer'
        } disabled:opacity-50`}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="block truncate">
          {selected?.is_undefined_default ? label : selected?.name || label}
        </span>
        {!locked ? (
          <ChevronDown
            className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        ) : null}
      </button>

      {open && !locked ? (
        <div className="absolute z-30 mt-1 w-full min-w-[220px] rounded-xl border border-[#D1D1D1] bg-white shadow-md overflow-hidden">
          <div className="px-4 py-2.5 border-b border-[#D1D1D1] text-sm font-semibold text-gray-900">
            {label}
          </div>
          <ul className="max-h-52 overflow-y-auto py-1" role="listbox">
            {options.map((option) => {
              const isSelected = option.id === selectedId;
              return (
                <li key={option.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      onSelect(option.id);
                      setOpen(false);
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                  >
                    <span
                      className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                        isSelected ? 'border-[#655FA8]' : 'border-gray-300'
                      }`}
                    >
                      {isSelected ? <span className="w-2 h-2 rounded-full bg-[#655FA8]" /> : null}
                    </span>
                    <span className="truncate">{option.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-[#D1D1D1] p-3 flex items-center gap-2">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleAdd();
                }
              }}
              placeholder={addPlaceholder}
              className="flex-1 min-w-0 rounded-lg border border-[#D1D1D1] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3191C8]/30"
            />
            <button
              type="button"
              onClick={() => void handleAdd()}
              disabled={!draft.trim() || adding}
              className="shrink-0 w-9 h-9 rounded-lg border border-[#D1D1D1] text-gray-500 hover:text-[#655FA8] hover:border-[#655FA8]/30 disabled:opacity-50"
              aria-label={`Add ${label.toLowerCase()}`}
            >
              <Plus className="w-4 h-4 mx-auto" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const RecipeCardTabControls: React.FC<RecipeCardTabControlsProps> = ({
  tabs,
  activeTabId,
  brands,
  formats,
  canSave,
  busy = false,
  onSelectTab,
  onCreateTab,
  onRenameTab,
  onDeleteTab,
  onSelectBrand,
  onSelectFormat,
  onCreateBrand,
  onCreateFormat,
  onGenerateFormatIdeas,
  showBrandFormat = true,
  headerTrailing,
}) => {
  const [openTabMenuId, setOpenTabMenuId] = useState<number | null>(null);
  const [renamingTabId, setRenamingTabId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const tabMenuButtonRef = useRef<HTMLButtonElement>(null);

  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? null;

  const startRename = (tab: RecipeCardTabItem) => {
    setRenamingTabId(tab.id);
    setRenameValue(tab.tab_name);
    setOpenTabMenuId(null);
  };

  const commitRename = () => {
    if (renamingTabId == null) return;
    const trimmed = renameValue.trim();
    if (trimmed) {
      onRenameTab(renamingTabId, trimmed);
    }
    setRenamingTabId(null);
    setRenameValue('');
  };

  const cancelRename = () => {
    setRenamingTabId(null);
    setRenameValue('');
  };

  const handleSelectTab = (tabId: number) => {
    if (renamingTabId != null && renamingTabId !== tabId) {
      commitRename();
    }
    if (tabId !== activeTabId) {
      onSelectTab(tabId);
    }
  };

  useEffect(() => {
    if (renamingTabId != null && renamingTabId !== activeTabId) {
      cancelRename();
    }
  }, [activeTabId, renamingTabId]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-2 border-b border-[#D1D1D1] pb-0 flex-1 min-w-0">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          return (
            <div key={tab.id} className="relative flex items-end">
              {renamingTabId === tab.id && isActive ? (
                <input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename();
                    if (e.key === 'Escape') cancelRename();
                  }}
                  className="mb-0.5 rounded-t-lg border border-[#D1D1D1] px-3 py-2 text-sm min-w-[120px] outline-none focus:border-[#655FA8] focus:ring-2 focus:ring-[#3191C8]/30"
                />
              ) : (
                <div
                  className={`flex items-center rounded-t-lg border text-sm transition-colors ${
                    isActive ? recipeTabActiveClass : `${recipeTabInactiveClass} border hover:border-[#D1D1D1]`
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleSelectTab(tab.id)}
                    className="px-4 py-2.5 outline-none focus-visible:ring-2 focus-visible:ring-[#3191C8]/30 rounded-tl-lg"
                  >
                    {tab.tab_name}
                  </button>
                  {isActive ? (
                    <div className="relative pr-1">
                      <button
                        ref={tabMenuButtonRef}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTabMenuId((current) => (current === tab.id ? null : tab.id));
                        }}
                        className="p-1.5 rounded text-gray-500 hover:bg-gray-100"
                        aria-label="Tab options"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                      <DropdownMenu
                        open={openTabMenuId === tab.id}
                        onClose={() => setOpenTabMenuId(null)}
                        anchorRef={tabMenuButtonRef}
                        minWidth="9rem"
                      >
                        <MenuItem onClick={() => startRename(tab)}>
                          <span className="inline-flex items-center gap-2.5">
                            <RotateCcw className="w-4 h-4 text-gray-500 shrink-0" aria-hidden />
                            Rename
                          </span>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setOpenTabMenuId(null);
                            onDeleteTab(tab.id);
                          }}
                          disabled={tabs.length <= 1}
                          destructive
                        >
                          <span className="inline-flex items-center gap-2.5">
                            <Trash2 className="w-4 h-4 text-gray-500 shrink-0" aria-hidden />
                            Delete
                          </span>
                        </MenuItem>
                      </DropdownMenu>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={onCreateTab}
          disabled={!canSave || busy}
          className={recipeAddTabButtonClass}
          aria-label="Add tab"
        >
          <Plus className="w-4 h-4" />
        </button>
        </div>
        {headerTrailing ? <div className="shrink-0 mb-2">{headerTrailing}</div> : null}
      </div>

      {activeTab && showBrandFormat ? (
        <div className="flex flex-wrap items-center gap-3">
          <CatalogDropdown
            label="Brand"
            options={brands}
            selectedId={activeTab.brand}
            locked={activeTab.brand_locked}
            disabled={!canSave || busy}
            addPlaceholder="Add a brand"
            onSelect={(brandId) => onSelectBrand(activeTab.id, brandId)}
            onAdd={onCreateBrand}
          />
          <CatalogDropdown
            label="Format"
            options={formats}
            selectedId={activeTab.format}
            locked={activeTab.format_locked}
            disabled={!canSave || busy}
            addPlaceholder="Add a format"
            onSelect={(formatId) => onSelectFormat(activeTab.id, formatId)}
            onAdd={onCreateFormat}
          />
          {onGenerateFormatIdeas ? (
            <button
              type="button"
              onClick={onGenerateFormatIdeas}
              className="text-sm font-medium text-[#655FA8] underline underline-offset-2 hover:text-[#544fa0] transition-colors"
            >
              Generate format ideas
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default RecipeCardTabControls;
